import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

/** Shape of `GET .../active-package` success `data` (matches super-admin rules service). */
export interface ActiveRulePackageInfo {
  version_id: string;
  version_seq: number;
  country_code: string;
  configuration: unknown;
  activated_at: string;
}

export interface CountryPackOption {
  code: string;
  name: string;
  /** ISO 3166-1 alpha-2 bridge to Agent.country_iso2; null for packs (e.g. CUSTOM) that resolve no country. */
  primary_iso2: string | null;
  status: 'ACTIVE' | 'BETA' | 'DISABLED';
}

/**
 * A row of the country_packs master.
 *
 * `code` is a pack code ('INDIA'), NOT an ISO country code — `primary_iso2` is the bridge to
 * the ISO countries table. Mixing the two is the easiest mistake to make here.
 */
export interface CountryPackRecord {
  id: number;
  code: string;
  display_name: string;
  primary_iso2: string | null;
  applies_to_iso2: string[];
  default_locale: string;
  supported_locales: string[];
  default_currency: string | null;
  default_timezone: string | null;
  date_format: string;
  name_script: 'LATIN' | 'ARABIC' | 'DEVANAGARI' | 'CJK' | 'MIXED';
  text_direction: 'LTR' | 'RTL';
  status: 'ACTIVE' | 'BETA' | 'DISABLED';
  is_platform_pack: boolean;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by?: number | null;
  created_at: string;
  updated_at: string;
}

export type RulePackageStatus = 'DRAFT' | 'PUBLISHED';

/** A rule package version row as returned by the rules repository. `id` is a UUID string. */
export interface RulePackageVersion {
  id: string;
  tenant_id: number;
  product_id: number;
  country_code: string;
  version_seq: number;
  status: RulePackageStatus;
  payload: unknown;
  compliance_pack_code: string;
  field_diff: Record<string, { old: unknown; new: unknown }> | null;
  restore_tag: string | null;
  published_at: string | null;
  created_at: string;
  created_by_user_id: number | null;
}

export interface VersionListPayload {
  items: RulePackageVersion[];
  total: number;
  page: number;
  page_size: number;
}

export interface ComplianceLockInfo {
  id: number;
  country_code: string;
  parameter_key: string;
  minimum_value: unknown;
  regulation: string;
  regulation_article: string | null;
  description: string | null;
}

export interface PreviewResult {
  is_preview: boolean;
  is_valid: boolean;
  validation_errors: string[];
  compliance_violations: Array<{
    parameter: string;
    required: unknown;
    provided: unknown;
    regulation: string;
  }>;
  configuration: unknown;
}


/** A control-plane catalog row. Keys vary by entity; the catalog descriptor names them. */
export type EntityRow = Record<string, unknown>;

/** A prompt code the AI service reads, with the variables its call site can fill. */
export interface PromptCodeSpec {
  code: string;
  description: string;
  variables: Array<{ name: string; required: boolean; description: string }>;
}

@Injectable({ providedIn: 'root' })
export class RulesApiService {
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  /**
   * Bootstrap CRE for tenant × product × country (creates v1 from defaults, publishes, sets active).
   * `POST /rules/tenant/:tenantId/product/:productId/country/:countryCode/activate` — no body.
   */
  activateCountryPack(
    tenantId: number,
    productId: number,
    countryCode: string,
  ): Observable<ApiEnvelope<{ version_id: string; version_seq: number; country_code: string }>> {
    const cc = encodeURIComponent(countryCode.trim());
    return this.http.post<
      ApiEnvelope<{ version_id: string; version_seq: number; country_code: string }>
    >(`${this.base}/rules/tenant/${tenantId}/product/${productId}/country/${cc}/activate`, {});
  }

  getActivePackage(
    tenantId: number,
    productId: number,
    countryCode: string,
  ): Observable<ApiEnvelope<ActiveRulePackageInfo>> {
    const cc = encodeURIComponent(countryCode.trim());
    return this.http.get<ApiEnvelope<ActiveRulePackageInfo>>(
      `${this.base}/rules/tenant/${tenantId}/product/${productId}/country/${cc}/active-package`,
    );
  }

  listCountryPacks(): Observable<ApiEnvelope<CountryPackOption[]>> {
    return this.http.get<ApiEnvelope<CountryPackOption[]>>(`${this.base}/rules/country-packs`);
  }

  // ── Country pack master ───────────────────────────────────────────────────
  // listCountryPacks() above is the {code,name} dropdown; these read and write the
  // country_packs table that makes onboarding a country a data change.

  listCountryPackRecords(): Observable<ApiEnvelope<CountryPackRecord[]>> {
    return this.http.get<ApiEnvelope<CountryPackRecord[]>>(
      `${this.base}/rules/country-packs/records`,
    );
  }

  listDeletedCountryPackRecords(): Observable<ApiEnvelope<CountryPackRecord[]>> {
    return this.http.get<ApiEnvelope<CountryPackRecord[]>>(
      `${this.base}/rules/country-packs/records/deleted`,
    );
  }

  createCountryPackRecord(
    body: Partial<CountryPackRecord>,
  ): Observable<ApiEnvelope<CountryPackRecord>> {
    return this.http.post<ApiEnvelope<CountryPackRecord>>(
      `${this.base}/rules/country-packs/records`,
      body,
    );
  }

  updateCountryPackRecord(
    code: string,
    body: Partial<CountryPackRecord>,
  ): Observable<ApiEnvelope<CountryPackRecord>> {
    return this.http.patch<ApiEnvelope<CountryPackRecord>>(
      `${this.base}/rules/country-packs/records/${encodeURIComponent(code)}`,
      body,
    );
  }

  deleteCountryPackRecord(code: string): Observable<ApiEnvelope<void>> {
    return this.http.delete<ApiEnvelope<void>>(
      `${this.base}/rules/country-packs/records/${encodeURIComponent(code)}`,
    );
  }

  restoreCountryPackRecord(code: string): Observable<ApiEnvelope<CountryPackRecord>> {
    return this.http.patch<ApiEnvelope<CountryPackRecord>>(
      `${this.base}/rules/country-packs/records/${encodeURIComponent(code)}/restore`,
      {},
    );
  }

  listVersions(
    tenantId: number,
    productId: number,
    countryCode: string,
    page = 1,
    pageSize = 20,
  ): Observable<ApiEnvelope<VersionListPayload>> {
    const cc = encodeURIComponent(countryCode.trim());
    return this.http.get<ApiEnvelope<VersionListPayload>>(
      `${this.base}/rules/tenant/${tenantId}/product/${productId}/country/${cc}/packages`,
      { params: { page, page_size: pageSize } as any },
    );
  }

  getVersionById(id: string): Observable<ApiEnvelope<RulePackageVersion>> {
    return this.http.get<ApiEnvelope<RulePackageVersion>>(`${this.base}/rules/packages/${id}`);
  }

  publishVersion(id: string): Observable<ApiEnvelope<RulePackageVersion>> {
    return this.http.post<ApiEnvelope<RulePackageVersion>>(`${this.base}/rules/packages/${id}/publish`, {});
  }

  restoreVersion(id: string): Observable<ApiEnvelope<RulePackageVersion>> {
    return this.http.post<ApiEnvelope<RulePackageVersion>>(`${this.base}/rules/packages/${id}/restore`, {});
  }

  previewPackage(
    tenantId: number,
    productId: number,
    countryCode: string,
    config: unknown,
  ): Observable<ApiEnvelope<PreviewResult>> {
    const cc = encodeURIComponent(countryCode.trim());
    return this.http.post<ApiEnvelope<PreviewResult>>(
      `${this.base}/rules/tenant/${tenantId}/product/${productId}/country/${cc}/packages/preview`,
      config,
    );
  }

  saveVersion(
    tenantId: number,
    productId: number,
    country: string,
    config: unknown,
  ): Observable<ApiEnvelope<RulePackageVersion>> {
    return this.http.post<ApiEnvelope<RulePackageVersion>>(
      `${this.base}/rules/tenant/${tenantId}/product/${productId}/country/${country}/packages`,
      config,
    );
  }

  // ── Document registry ───────────────────────────────────────────────────

  listDocumentTypes(pack?: string): Observable<ApiEnvelope<EntityRow[]>> {
    const query = pack ? `?pack=${encodeURIComponent(pack)}` : '';
    return this.http.get<ApiEnvelope<EntityRow[]>>(
      `${this.base}/rules/document-types/records${query}`,
    );
  }

  listDeletedDocumentTypes(): Observable<ApiEnvelope<EntityRow[]>> {
    return this.http.get<ApiEnvelope<EntityRow[]>>(
      `${this.base}/rules/document-types/records/deleted`,
    );
  }

  createDocumentType(body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/document-types/records`,
      body,
    );
  }

  updateDocumentType(
    code: string,
    body: Record<string, unknown>,
  ): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/document-types/records/${encodeURIComponent(code)}`,
      body,
    );
  }

  deleteDocumentType(code: string): Observable<ApiEnvelope<void>> {
    return this.http.delete<ApiEnvelope<void>>(
      `${this.base}/rules/document-types/records/${encodeURIComponent(code)}`,
    );
  }

  restoreDocumentType(code: string): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/document-types/records/${encodeURIComponent(code)}/restore`,
      {},
    );
  }

  syncDocumentTypes(): Observable<ApiEnvelope<unknown>> {
    return this.http.post<ApiEnvelope<unknown>>(`${this.base}/rules/document-types/sync`, {});
  }

  // ── Compliance locks ────────────────────────────────────────────────────

  listComplianceLockRecords(country?: string): Observable<ApiEnvelope<EntityRow[]>> {
    const query = country ? `?country=${encodeURIComponent(country)}` : '';
    return this.http.get<ApiEnvelope<EntityRow[]>>(
      `${this.base}/rules/compliance-locks/records${query}`,
    );
  }

  listDeletedComplianceLockRecords(country?: string): Observable<ApiEnvelope<EntityRow[]>> {
    const query = country ? `?country=${encodeURIComponent(country)}` : '';
    return this.http.get<ApiEnvelope<EntityRow[]>>(
      `${this.base}/rules/compliance-locks/records/deleted${query}`,
    );
  }

  upsertComplianceLock(body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/compliance-locks/records`,
      body,
    );
  }

  deactivateComplianceLock(country: string, key: string): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/compliance-locks/records/${encodeURIComponent(country)}/${encodeURIComponent(key)}/deactivate`,
      {},
    );
  }

  deleteComplianceLock(id: number): Observable<ApiEnvelope<void>> {
    return this.http.delete<ApiEnvelope<void>>(
      `${this.base}/rules/compliance-locks/records/${id}`,
    );
  }

  restoreComplianceLock(id: number): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/compliance-locks/records/${id}/restore`,
      {},
    );
  }

  // ── Prompt templates ────────────────────────────────────────────────────

  getPromptCatalog(): Observable<ApiEnvelope<PromptCodeSpec[]>> {
    return this.http.get<ApiEnvelope<PromptCodeSpec[]>>(`${this.base}/rules/prompts/catalog`);
  }

  listPromptRecords(code?: string, pack?: string): Observable<ApiEnvelope<EntityRow[]>> {
    const params = [
      code ? `code=${encodeURIComponent(code)}` : '',
      pack ? `pack=${encodeURIComponent(pack)}` : '',
    ].filter(Boolean);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<ApiEnvelope<EntityRow[]>>(`${this.base}/rules/prompts/records${query}`);
  }

  listDeletedPromptRecords(code?: string, pack?: string): Observable<ApiEnvelope<EntityRow[]>> {
    const params = [
      code ? `code=${encodeURIComponent(code)}` : '',
      pack ? `pack=${encodeURIComponent(pack)}` : '',
    ].filter(Boolean);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<ApiEnvelope<EntityRow[]>>(
      `${this.base}/rules/prompts/records/deleted${query}`,
    );
  }

  createPromptDraft(body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(`${this.base}/rules/prompts/records`, body);
  }

  updatePromptDraft(
    id: number,
    body: Record<string, unknown>,
  ): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/prompts/records/${id}`,
      body,
    );
  }

  publishPrompt(id: number): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/prompts/records/${id}/publish`,
      {},
    );
  }

  deletePromptRecord(id: number): Observable<ApiEnvelope<void>> {
    return this.http.delete<ApiEnvelope<void>>(`${this.base}/rules/prompts/records/${id}`);
  }

  restorePromptRecord(id: number): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/prompts/records/${id}/restore`,
      {},
    );
  }

  getComplianceLocks(countryCode: string): Observable<ApiEnvelope<ComplianceLockInfo[]>> {
    return this.http.get<ApiEnvelope<ComplianceLockInfo[]>>(`${this.base}/rules/compliance-locks`, {
      params: { country_code: countryCode } as any,
    });
  }

  // ── LLM models ───────────────────────────────────────────────────────────

  listLlmModels(): Observable<ApiEnvelope<EntityRow[]>> {
    return this.http.get<ApiEnvelope<EntityRow[]>>(`${this.base}/rules/llm-models`);
  }

  listDeletedLlmModels(): Observable<ApiEnvelope<EntityRow[]>> {
    return this.http.get<ApiEnvelope<EntityRow[]>>(`${this.base}/rules/llm-models/deleted`);
  }

  createLlmModel(body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(`${this.base}/rules/llm-models`, body);
  }

  updateLlmModel(id: number, body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(`${this.base}/rules/llm-models/${id}`, body);
  }

  deleteLlmModel(id: number): Observable<ApiEnvelope<void>> {
    return this.http.delete<ApiEnvelope<void>>(`${this.base}/rules/llm-models/${id}`);
  }

  restoreLlmModel(id: number): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/llm-models/${id}/restore`,
      {},
    );
  }

  // ── LLM config records ──────────────────────────────────────────────────

  listLlmConfigRecords(): Observable<ApiEnvelope<EntityRow[]>> {
    return this.http.get<ApiEnvelope<EntityRow[]>>(`${this.base}/rules/llm-config/records`);
  }

  /**
   * Creates the default task configs on an environment that has none.
   *
   * The server has always had this; nothing called it, so a fresh deployment showed an empty page
   * with no way out of it.
   */
  seedLlmConfigDefaults(): Observable<ApiEnvelope<{ created: number }>> {
    return this.http.post<ApiEnvelope<{ created: number }>>(
      `${this.base}/rules/llm-config/seed-defaults`,
      {},
    );
  }

  createLlmConfigRecord(body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(`${this.base}/rules/llm-config/records`, body);
  }

  updateLlmConfigRecord(id: number, body: Record<string, unknown>): Observable<ApiEnvelope<EntityRow>> {
    return this.http.patch<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/llm-config/records/${id}`,
      body,
    );
  }

  publishLlmConfigRecord(id: number): Observable<ApiEnvelope<EntityRow>> {
    return this.http.post<ApiEnvelope<EntityRow>>(
      `${this.base}/rules/llm-config/records/${id}/publish`,
      {},
    );
  }
}
